import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useApolloClient, useQuery } from '@apollo/client';

import { queryStrings } from '@trqk/resolvers';
import {
    getGapAnalysisLocalStorageKey,
    getInLocalStorage,
    setInLocalStorage,
    getURLSearchParams,
    updateAndReplaceURLSearchParams,
} from '@trqk/utils';

import { ManageGapAnalysisBody } from './components';
import {
    useGapAnalysisTools,
    useUploadDetections,
    useEntities,
} from '../../../hooks';
import { URL_PARAMS } from '../../../constants';

const { GET_ACTIVE_LOG_POLLS, GET_SESSION_CURRENT_USER } = queryStrings;

/**
 * Returns a modal that allows the user to create or edit a Gap Analysis report. This is a
 * multi-step process that requires detailed user interaction. This modal is unique because its
 * state is managed by a state machine - see the machine definition in:
 * packages/web/src/hooks/useGapAnalysisTools/gapAnalysisMachine.ts/gapAnalysisMachine.ts.
 * @param props - {@link ModalContentProps}
 */
export const ManageGapAnalysis = ({
    remove,
}: ModalContentProps<Record<string, never>>): JSX.Element => {
    const { context, send } = useGapAnalysisTools();
    const { selectedEntityId } = useEntities();
    const currentUserQuery = useQuery<{ session: { currentUser: User } }>(
        GET_SESSION_CURRENT_USER,
    );
    const userId = currentUserQuery?.data?.session?.currentUser?.id;

    // It's possible that the user has already started working on a gap analysis report, and clicked
    // the "Upload Statement" link from the SetUp step. In this case, we need to restore the state
    // machine context to its prior value before they clicked the "Upload Statement" link.
    const history = useHistory();
    const {
        location: { search },
    } = history;

    // If the user clicked the "go back" button in CreateGapAnalysisHeader within ManageStatements,
    // the URL search param for `continue_create_gap_analysis` will be "true".
    const searchParams = getURLSearchParams({
        configs: [
            {
                paramKey: URL_PARAMS.CONTINUE_MANAGE_GAP_ANALYSIS,
                defaultValue: 'false',
            },
            {
                paramKey: URL_PARAMS.GAP_ANALYSIS_MODE,
                defaultValue: null,
            },
        ],
        search,
    });

    // If we find the `continue_create_gap_analysis` URL search param is "true", get the prior
    // context from local storage. It was previously persisted as a JSON.stringified() object in
    // packages/web/src/components/modals/CreateGapAnalysis/components/steps/SetUp/SetUp.tsx.
    const priorContextString =
        userId !== undefined &&
        searchParams[URL_PARAMS.CONTINUE_MANAGE_GAP_ANALYSIS] === 'true'
            ? getInLocalStorage<string>(
                  getGapAnalysisLocalStorageKey(userId, selectedEntityId),
              )
            : null;

    useEffect(() => {
        if (priorContextString !== null && userId !== undefined) {
            // Try to restore the prior context when appropriate
            try {
                const priorContext: GapAnalysisContext =
                    JSON.parse(priorContextString);

                send({
                    type: 'REPLACE_CONTEXT',
                    value: {
                        newContext: {
                            ...priorContext,
                            loading: false,
                        },
                    },
                });
            } catch {
                // If this fails, we do nothing - the user will have to start over from the
                // beginning of the Create Gap Analysis workflow.
            }

            // Either way, remove the URL search param and delete the prior context from
            // localStorage
            updateAndReplaceURLSearchParams(history, [
                {
                    key: URL_PARAMS.CONTINUE_MANAGE_GAP_ANALYSIS,
                    value: null,
                },
            ]);

            const localStorageKey = getGapAnalysisLocalStorageKey(
                userId,
                selectedEntityId,
            );

            setInLocalStorage<string>(localStorageKey, () => null);
        }
    }, [priorContextString, userId, selectedEntityId]);

    const { cache } = useApolloClient();
    const { inProgressTasks } = useUploadDetections();

    const hasActiveUploads = inProgressTasks.length > 0;

    // Inform the cache that there are active detection uploads, so that LogDataProvider uses the
    // short log polling interval
    useEffect(() => {
        if (hasActiveUploads) {
            cache.writeQuery({
                query: GET_ACTIVE_LOG_POLLS,
                data: {
                    uiState: {
                        __typename: 'UIState',
                        activeLogPolls: {
                            __typename: 'ActiveLogPolls',
                            uploadDetections: true,
                        },
                    },
                },
            });
        }
    }, [hasActiveUploads, cache]);

    useEffect(
        // When the CreateGapAnalysis modal unmounts, update the cache so that LogDataProvider
        // uses the long polling interval it there are no other active polls
        () => () => {
            cache.writeQuery({
                query: GET_ACTIVE_LOG_POLLS,
                data: {
                    uiState: {
                        __typename: 'UIState',
                        activeLogPolls: {
                            __typename: 'ActiveLogPolls',
                            uploadDetections: false,
                        },
                    },
                },
            });
        },
        [cache],
    );

    return (
        <ManageGapAnalysisBody context={context} remove={remove} send={send} />
    );
};
